import { Box, Typography } from '@mui/material';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import useFileUploader from 'customHook/useFileUpload';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { ModalAction } from 'model/modal.model';
import { ChangeEvent, Dispatch, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation } from 'react-router-dom';
import zeplinColor from 'theme/Color';
import FileRow from './FileRow/FileRow';

export const DragDropModal = ({
  isModalOpen,
  modalDispatch,
  fetchDocuments,
  startOperation,
  completeOperation,
}: {
  isModalOpen: boolean;
  modalDispatch: Dispatch<ModalAction>;
  fetchDocuments: () => Promise<void>;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) => {
  const client = useLocation();
  const clientId = client.state?.client.AccountId;
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { uploadSelectedFiles } = useFileUploader(clientId, startOperation, completeOperation);

  const onDrop = (acceptedFiles: File[]) => {
    setSelectedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  };

  const isDropDownDisabled = selectedFiles.length >= 10;
  const maxNumberOfFilesAllowed = 10 - selectedFiles.length;
  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    // react-dropzone's maxSize is bytes. Backend caps at 50 MB.
    maxSize: 50 * 1024 * 1024,
    maxFiles: maxNumberOfFilesAllowed,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],
      'video/mp4': ['.mp4', '.m4v'],
      'video/webm': ['.webm'],
      'video/quicktime': ['.mov', '.qt'],
      'video/x-msvideo': ['.avi'],
      'video/mp2t': ['.ts', '.mts', '.m2ts'],
      'video/ogg': ['.ogv'],
      'video/x-ms-wmv': ['.wmv'],
    },
    disabled: isDropDownDisabled,
  });
  const DragNDropDescription = isDragActive
    ? 'Drop the files here ...'
    : 'Drag and drop your file here or click to select files';

  const handleUploadFiles = async (_: ChangeEvent<HTMLInputElement>) => {
    await uploadSelectedFiles(selectedFiles);
    modalDispatch({ type: 'CLOSE', payload: 'upload' });
    await fetchDocuments();
  };

  const handleDeleteFile = (index: number): void => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <CustomModal
      handleClose={() => modalDispatch({ type: 'CLOSE', payload: 'upload' })}
      open={isModalOpen}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '600px',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px',
            backgroundColor: zeplinColor.Neutral1,
            borderRadius: '14px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant='responsiveHeading2' color={zeplinColor.BasicBlack}>
              Upload Files
            </Typography>
            <CustomButton
              style={{
                width: '24px',
                height: '24px',
                minWidth: '24px',
                padding: 0,
                '.MuiButton-startIcon': {
                  margin: 0,
                },
              }}
              variant='text'
              text=''
              icon={
                <img
                  width={'24px'}
                  height={'24px'}
                  src={'/assets/cross.svg'}
                  alt='logo'
                  style={{ objectFit: 'cover', margin: 0 }}
                />
              }
              onClick={() => modalDispatch({ type: 'CLOSE', payload: 'upload' })}
              disabled={false}
            />
          </Box>

          <Box sx={{ width: '100%', marginTop: '24px' }}>
            <div
              style={{
                borderStyle: 'dashed',
                borderWidth: '1px',
                borderColor: zeplinColor.Neutral6,
                backgroundColor: zeplinColor.Neutral2,
                borderRadius: '14px',
                width: '100%',
                padding: '24px',
                cursor: !isDropDownDisabled ? 'pointer' : 'default',
              }}
              {...getRootProps({ className: 'dropzone upload-btn' })}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                }}
              >
                <img
                  width={'24px'}
                  height={'24px'}
                  src={'/assets/upload.svg'}
                  alt='logo'
                  style={{ objectFit: 'fill' }}
                />
                <Typography
                  variant='body2'
                  color={!isDropDownDisabled ? zeplinColor.BasicBlack : zeplinColor.Neutral8}
                  sx={{
                    marginTop: '24px',
                  }}
                >
                  {DragNDropDescription}
                </Typography>

                <Typography
                  variant='responsiveLabel1'
                  color={zeplinColor.Neutral8}
                  sx={{
                    marginTop: '8px',
                  }}
                >
                  Max file size: 50MB | Support format: pdf, jpg, png, gif, webp, svg
                </Typography>
              </Box>
            </div>
          </Box>
          {selectedFiles.length > 0 && (
            <Typography
              variant='responsiveBody3'
              color={zeplinColor.BasicBlack}
              sx={{
                width: '100%',
                marginTop: '12px',
                marginBottom: '12px',
                display: 'inline-flex',
                justifyContent: 'flex-start',
              }}
            >
              Selected files: {selectedFiles.length}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '600px',
              padding: selectedFiles.length ? '12px 24px 24px 24px' : '',
              maxHeight: '380px',
              overflow: 'scroll',
            }}
          >
            {selectedFiles.map((file, index) => (
              <FileRow
                file={file}
                index={index}
                key={file.name + Math.random()}
                handleDeleteFile={handleDeleteFile}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              gap: '16px',
              marginTop: '24px',
            }}
          >
            <CustomButton
              disabled={false}
              onClick={() => {
                clearFiles();
                modalDispatch({ type: 'CLOSE', payload: 'upload' });
              }}
              variant='outlined'
              text='Cancel'
              color='secondary'
              style={{
                borderRadius: '10px',
                height: '48px',
                padding: '14px 32px',
                color: zeplinColor.BasicBlack,
                borderColor: zeplinColor.BasicBlack,
                '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral5 },
                ':hover': { backgroundColor: zeplinColor.Neutral4 },
              }}
            />
            <CustomButton
              disabled={!selectedFiles.length}
              onClick={handleUploadFiles}
              variant='contained'
              color='primary'
              text='Upload'
              style={{
                borderRadius: '10px',
                height: '48px',
                padding: '14px 32px',
                color: `${!selectedFiles.length ? zeplinColor.Neutral8 : zeplinColor.Neutral1}`,
                '&.Mui-focusVisible': { backgroundColor: zeplinColor.Brand1Active },
                ':hover': { backgroundColor: zeplinColor.Brand1Hover },
              }}
            />
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};
